import {
  createContext,
  FormEvent,
  ReactNode,
  useEffect,
  useContext,
  useState,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { FilterField } from "../../../components/FilterModal/types";
import { lojaService } from "../../../services/lojaService";
import {
  tamanhoService,
  Tamanho,
  TamanhoFormData,
} from "../../../services/tamanhoService";
import { COLORS } from "../../../constants";
import { tamanhoFilterFields } from "../constants/filterFields";

type TamanhoFormState = {
  id: number | undefined;
  nome: string;
  lojaId: number;
};

type TamanhoFilters = {
  nome: string;
  lojaId: string;
};

interface TamanhosContextType {
  tamanhos: Tamanho[];
  lojas: Array<{ id: number; nome: string }>;
  isLoading: boolean;
  isModalOpen: boolean;
  isFilterModalOpen: boolean;
  formState: TamanhoFormState;
  filterFields: FilterField[];
  tableData: Array<[string, string]>;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  activeFiltersCount: number;
  isSaving: boolean;
  openFilterModal: () => void;
  closeFilterModal: () => void;
  openCreateModal: () => void;
  openEditModal: (item: Tamanho) => void;
  closeModal: () => void;
  handleDelete: (item: Tamanho) => Promise<void>;
  handleSubmit: (event: FormEvent<HTMLFormElement>) => void;
  handleFilterFieldChange: (name: string, value: string) => void;
  handleSearch: () => void;
  handleClear: () => void;
  setFormNome: (nome: string) => void;
  setFormLojaId: (lojaId: number) => void;
  setCurrentPage: (page: number) => void;
}

const TamanhosContext = createContext<TamanhosContextType | undefined>(
  undefined
);

const getDefaultFormState = (): TamanhoFormState => ({
  id: undefined,
  nome: "",
  lojaId: 0,
});

const getDefaultFilters = (): TamanhoFilters => ({
  nome: "",
  lojaId: "",
});

export const TamanhosProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formState, setFormState] = useState<TamanhoFormState>(
    getDefaultFormState()
  );
  const [filters, setFilters] = useState<TamanhoFilters>(getDefaultFilters());
  const [appliedFilters, setAppliedFilters] =
    useState<TamanhoFilters>(getDefaultFilters());
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const { data: lojas = [] } = useQuery({
    queryKey: ["lojas-options"],
    queryFn: () => lojaService.getLojas(),
  });

  useEffect(() => {
    if (lojas.length > 0 && formState.lojaId === 0) {
      setFormState((prev) => ({ ...prev, lojaId: lojas[0].id }));
    }
  }, [lojas, formState.lojaId]);

  const { data: tamanhos = [], isLoading } = useQuery({
    queryKey: ["tamanhos", appliedFilters],
    queryFn: () =>
      tamanhoService.getTamanhos({
        nome: appliedFilters.nome || undefined,
        lojaId: appliedFilters.lojaId
          ? Number(appliedFilters.lojaId)
          : undefined,
      }),
  });

  const activeFiltersCount = Object.values(appliedFilters).filter(
    (value) => value !== null && value !== undefined && value !== ""
  ).length;

  const filterFields: FilterField[] = tamanhoFilterFields.map((field) => {
    const baseField = {
      ...field,
      value: filters[field.name as keyof TamanhoFilters] || "",
    };

    if (field.name === "lojaId") {
      return {
        ...baseField,
        options: lojas.map((loja) => ({ value: loja.id, label: loja.nome })),
      };
    }

    return baseField;
  });

  const getLojaNome = (lojaId: number) =>
    lojas.find((loja) => loja.id === lojaId)?.nome || "-";

  const orderedTamanhos = [...tamanhos].sort((a, b) => {
    const nomeCompare = a.nome.localeCompare(b.nome, "pt-BR", {
      sensitivity: "base",
    });

    if (nomeCompare !== 0) return nomeCompare;

    return getLojaNome(a.lojaId).localeCompare(getLojaNome(b.lojaId), "pt-BR", {
      sensitivity: "base",
    });
  });

  const totalItems = orderedTamanhos.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTamanhos = orderedTamanhos.slice(startIndex, endIndex);

  const tableData: Array<[string, string]> = paginatedTamanhos.map((item) => [
    item.nome,
    getLojaNome(item.lojaId),
  ]);

  useEffect(() => {
    if (totalPages === 0 && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const invalidateTamanhos = () => {
    queryClient.invalidateQueries({ queryKey: ["tamanhos"] });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormState(getDefaultFormState());
  };

  const createMutation = useMutation({
    mutationFn: (payload: TamanhoFormData) =>
      tamanhoService.createTamanho(payload),
    onSuccess: () => {
      toast.success("Tamanho cadastrado com sucesso.");
      invalidateTamanhos();
      closeModal();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: { id: number; data: TamanhoFormData }) =>
      tamanhoService.updateTamanho(payload.id, payload.data),
    onSuccess: () => {
      toast.success("Tamanho atualizado com sucesso.");
      invalidateTamanhos();
      closeModal();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => tamanhoService.deleteTamanho(id),
    onSuccess: () => {
      toast.success("Tamanho removido com sucesso.");
      invalidateTamanhos();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const openFilterModal = () => setIsFilterModalOpen(true);
  const closeFilterModal = () => setIsFilterModalOpen(false);

  const openCreateModal = () => {
    setFormState(getDefaultFormState());
    setIsModalOpen(true);
  };

  const openEditModal = (item: Tamanho) => {
    setFormState({
      id: item.id,
      nome: item.nome,
      lojaId: item.lojaId,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (item: Tamanho) => {
    const result = await Swal.fire({
      title: "Tem certeza?",
      text: `Deseja remover o tamanho "${item.nome}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: COLORS.PRIMARY,
      cancelButtonColor: COLORS.NEUTRAL_500,
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;
    await deleteMutation.mutateAsync(item.id);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nome = formState.nome.trim();
    if (!nome) {
      toast.error("Informe o nome do tamanho.");
      return;
    }

    if (!formState.lojaId) {
      toast.error("Selecione a loja.");
      return;
    }

    const payload: TamanhoFormData = {
      nome,
      lojaId: formState.lojaId,
    };

    if (formState.id) {
      updateMutation.mutate({ id: formState.id, data: payload });
      return;
    }

    createMutation.mutate(payload);
  };

  const handleFilterFieldChange = (name: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = () => {
    setAppliedFilters(filters);
    setCurrentPage(1);
    setIsFilterModalOpen(false);
  };

  const handleClear = () => {
    const empty = getDefaultFilters();
    setFilters(empty);
    setAppliedFilters(empty);
    setCurrentPage(1);
  };

  const setFormNome = (nome: string) => {
    setFormState((prev) => ({ ...prev, nome }));
  };

  const setFormLojaId = (lojaId: number) => {
    setFormState((prev) => ({ ...prev, lojaId }));
  };

  return (
    <TamanhosContext.Provider
      value={{
        tamanhos: paginatedTamanhos,
        lojas,
        isLoading,
        isModalOpen,
        isFilterModalOpen,
        formState,
        filterFields,
        tableData,
        currentPage,
        totalPages,
        totalItems,
        itemsPerPage,
        activeFiltersCount,
        isSaving: createMutation.isPending || updateMutation.isPending,
        openFilterModal,
        closeFilterModal,
        openCreateModal,
        openEditModal,
        closeModal,
        handleDelete,
        handleSubmit,
        handleFilterFieldChange,
        handleSearch,
        handleClear,
        setFormNome,
        setFormLojaId,
        setCurrentPage,
      }}
    >
      {children}
    </TamanhosContext.Provider>
  );
};

export const useTamanhosPage = () => {
  const context = useContext(TamanhosContext);
  if (!context) {
    throw new Error("useTamanhosPage must be used within TamanhosProvider");
  }
  return context;
};
